import { useCallback, useEffect, useState } from "react";
import DateRangeModal from "./CropMonitoring/DateRangeModal";
import { useT } from "../../../i18n";

// 작물분석·에너지 탭이 같이 쓰는 조회 기간 · 갱신 줄.
// 원래 조회 탭 하나에 두 섹션이 같이 있어서 툴바도 하나였다. 에너지를 탭으로 떼면서
// 툴바를 여기로 빼고, 두 탭이 각자 기간을 갖는다(한쪽을 바꿔도 다른 쪽은 그대로).

const DAY_MS = 86400000;

export const formatApiDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatRangeDate = (date) => `${date.getMonth() + 1}.${date.getDate()}`;

const startOfDaysAgo = (days) => new Date(Date.now() - days * DAY_MS);

const formatClock = (date) =>
  date
    ? `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
    : "—";

const CalendarGlyph = () => (
  <svg className="calendar-glyph" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <rect x="2" y="3.25" width="12" height="11" rx="2.4" />
    <path d="M2 6.75h12M5.25 1.75v2.6M10.75 1.75v2.6" />
  </svg>
);

const RefreshGlyph = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" className="dt-refresh-glyph">
    <path d="M13.5 8a5.5 5.5 0 1 1-1.7-3.9" />
    <path d="M13.6 2.2v3.1h-3.1" />
  </svg>
);

// days 는 "오늘로부터 며칠 전까지"다. 오늘이 하루로 세어지므로 7일은 6 이다.
// 1일(오늘)은 하루에 여러 번 날린 날 회차만 따로 보려고 둔다 — 7일로 보면
// 다른 날 회차에 섞여 목록이 길어진다.
// 버튼 문구는 key(일수)로 t("data.range.presetDays") 를 불러 만든다.
const PRESETS = [
  { key: "1", days: 0 },
  { key: "7", days: 6 },
  { key: "30", days: 29 },
];

// 기본은 1일(오늘). 처음 열면 오늘 날린 회차만 보이게 한다.
// 에너지 탭은 "7" 로 시작한다 — 일별 집계는 1일이면 점 하나라 선이 안 그려진다.
export function useDateRange(initialPreset = "1", { autoAdvance = false } = {}) {
  const t = useT();
  const initial = PRESETS.find((item) => item.key === initialPreset) ?? PRESETS[0];
  const [preset, setPreset] = useState(initial.key);
  const [range, setRange] = useState(() => ({
    start: startOfDaysAgo(initial.days),
    end: new Date(),
  }));
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  // 새로고침 버튼이 같은 파라미터로도 다시 조회하게 만드는 nonce
  const [reloadToken, setReloadToken] = useState(0);

  const applyPreset = useCallback((item) => {
    setPreset(item.key);
    setRange({ start: startOfDaysAgo(item.days), end: new Date() });
  }, []);

  const applyCustom = useCallback((start, end) => {
    setPreset("custom");
    setRange({ start, end });
  }, []);

  const advanceToToday = useCallback(() => {
    const item = PRESETS.find((candidate) => candidate.key === preset);
    if (!autoAdvance || !item) return;
    const today = new Date();
    setRange((previous) => formatApiDate(previous.end) === formatApiDate(today)
      ? previous
      : { start: new Date(today.getTime() - item.days * DAY_MS), end: today });
  }, [autoAdvance, preset]);

  useEffect(() => {
    if (!autoAdvance || preset === "custom") return undefined;
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      timer = setTimeout(() => {
        advanceToToday();
        schedule();
      }, midnight.getTime() - Date.now());
    };
    const resume = () => {
      if (document.visibilityState !== "hidden") {
        advanceToToday();
        schedule();
      }
    };
    advanceToToday();
    schedule();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
    };
  }, [autoAdvance, preset, advanceToToday]);

  const reload = useCallback(() => {
    advanceToToday();
    setReloadToken((token) => token + 1);
  }, [advanceToToday]);

  const rangeLabel = `${formatRangeDate(range.start)} – ${formatRangeDate(range.end)}`;
  // 장비 칩 옆에 붙는 짧은 기간 표기
  const periodLabel =
    preset === "custom"
      ? rangeLabel
      : preset === "1"
        ? t("data.range.today")
        : t("data.range.lastDays", { days: preset });

  return {
    preset,
    range,
    from: formatApiDate(range.start),
    to: formatApiDate(range.end),
    rangeLabel,
    periodLabel,
    applyPreset,
    applyCustom,
    isRangeModalOpen,
    setIsRangeModalOpen,
    reloadToken,
    reload,
  };
}

// "HH:MM 기준" + 새로고침 버튼. 에너지 탭은 이걸 기간 줄에서 떼어 탭 맨 위에 둔다 —
// 새로고침은 추이 차트만이 아니라 탭 전체(실시간 숫자 · 타임라인)를 다시 불러오기 때문.
// before: 시각 왼쪽에 붙일 요소(작물분석 탭의 예찰보고서 버튼).
export function RefreshControl({ fetchedAt, isLoading, onReload, before = null }) {
  const t = useT();
  return (
    <div className="dt-refresh">
      {before}
      <span className="dt-refresh-time">
        {t("data.range.asOf", { time: formatClock(fetchedAt) })}
      </span>
      <button
        type="button"
        className="dt-refresh-button"
        aria-label={t("data.range.refresh")}
        disabled={isLoading}
        onClick={onReload}
      >
        <RefreshGlyph />
      </button>
    </div>
  );
}

export default function RangeToolbar({ dateRange, fetchedAt, isLoading, showRefresh = true, beforeTime = null }) {
  const t = useT();
  const {
    preset,
    range,
    rangeLabel,
    applyPreset,
    applyCustom,
    isRangeModalOpen,
    setIsRangeModalOpen,
    reload,
  } = dateRange;

  return (
    <>
      <div className="dt-toolbar">
        <div className="dt-seg" role="group" aria-label={t("data.range.label")}>
          {PRESETS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={preset === item.key}
              onClick={() => applyPreset(item)}
            >
              {t("data.range.presetDays", { days: item.key })}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={preset === "custom"}
            aria-haspopup="dialog"
            onClick={() => setIsRangeModalOpen(true)}
          >
            <CalendarGlyph />
            {preset === "custom" ? rangeLabel : t("data.range.custom")}
          </button>
        </div>

        {showRefresh ? <RefreshControl fetchedAt={fetchedAt} isLoading={isLoading} onReload={reload} before={beforeTime} /> : beforeTime}
      </div>

      {isRangeModalOpen && (
        <DateRangeModal
          setIsOpen={setIsRangeModalOpen}
          onChange={applyCustom}
          start={range.start}
          end={range.end}
          maxDate={new Date()}
        />
      )}
    </>
  );
}
